import { Box, Button, Container, TextField, Typography } from "@mui/material";
import axios from "axios";
import { Field, Form, Formik } from "formik";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import AuthContainer from "../../../components/AuthContainer";
import { emailValidation } from "../../../utils/validationSchemas";
import Logo from "../../../components/Logo";

export default function ResetPassword() {
  const [error, setError] = useState();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const navigate = useNavigate();

  const initialValues = {
    email: "",
    otp: "",
    password: "",
    password_confirmation: "",
  };

  const handleEmailSubmit = async (values) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/password/otp`,
        { email: values.email },
      );

      if (response.status === 200) {
        setEmail(values.email);
        setStep(2);
      } else {
        setError("Failed to send OTP. Please try again.");
      }
    } catch (error) {
      setError("Failed to send OTP. Please try again.");
    }
  };

  const handleOtpSubmit = async (values) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/password/reset/verify`,
        {
          email,
          otp: values.otp,
          password: values.password,
          password_confirmation: values.password_confirmation,
        },
      );

      if (response.status === 200) {
        navigate("/login");
      } else {
        setError("Invalid OTP. Please try again.");
      }
    } catch (error) {
      setError("Failed to verify OTP. Please try again.");
    }
  };

  return (
    <AuthContainer>
      <Container
        maxWidth="xs"
        sx={{ flex: 1, display: "flex", alignItems: "center" }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              flexDirection: "row",
              alignItems: "center",
              gap: 2,
              mb: 4,
            }}
          >
            <Logo />
          </Box>

          <Box sx={{ width: "100%", padding: 2 }}>
            <Typography variant="h4" gutterBottom textAlign="center">
              Reset Password
            </Typography>
            <Typography variant="body2" gutterBottom textAlign="center">
              Enter your email address and we will send you a one-time password
              (OTP) to reset your password.
            </Typography>
            {error && (
              <Box sx={{ width: "100%", mb: 2 }} color="error.light">
                <Typography color="error">{error}</Typography>
              </Box>
            )}
            <Formik
              initialValues={initialValues}
              validationSchema={Yup.object({
                email: emailValidation,
                password:
                  step === 2
                    ? Yup.string()
                        .required("Password is required")
                        .min(6, "Password must be at least 6 characters long")
                    : Yup.string().notRequired(),
                password_confirmation:
                  step === 2
                    ? Yup.string()
                        .oneOf(
                          [Yup.ref("password"), null],
                          "Passwords must match",
                        )
                        .required("Password confirmation is required")
                    : Yup.string().notRequired(),
                otp:
                  step === 2
                    ? Yup.string().min(6).max(6).required()
                    : Yup.string().notRequired(),
              })}
              onSubmit={step === 1 ? handleEmailSubmit : handleOtpSubmit}
            >
              {({ errors, touched }) => (
                <Form>
                  {step === 1 && ( // Email Input Step
                    <Field
                      name="email"
                      as={TextField}
                      placeholder="Email"
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                    />
                  )}
                  {step === 2 && ( // OTP and Password Input Step
                    <>
                      <Field
                        name="otp"
                        as={TextField}
                        placeholder="OTP"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        error={touched.otp && Boolean(errors.otp)}
                        helperText={touched.otp && errors.otp}
                      />
                      <Field
                        name="password"
                        as={TextField}
                        placeholder="New Password"
                        type="password"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        error={touched.password && Boolean(errors.password)}
                        helperText={touched.password && errors.password}
                      />
                      <Field
                        name="password_confirmation"
                        as={TextField}
                        placeholder="New Password Confirmation"
                        type="password"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        error={
                          touched.password_confirmation &&
                          Boolean(errors.password_confirmation)
                        }
                        helperText={
                          touched.password_confirmation &&
                          errors.password_confirmation
                        }
                      />
                    </>
                  )}
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="primary"
                    sx={{ mt: 3, py: 1.5, fontWeight: "bold" }}
                  >
                    {step === 1 ? "Send OTP" : "Reset Password"}
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="primary"
                    sx={{ mt: 3, py: 1.5, fontWeight: "bold" }}
                    onClick={() => navigate("/login")}
                  >
                    Cancel
                  </Button>
                </Form>
              )}
            </Formik>
          </Box>
        </Box>
      </Container>
    </AuthContainer>
  );
}
